import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material'
import {
  Restaurant,
  Assignment,
  Person,
  Logout,
  Warning
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import { differenceInDays, parseISO, addYears } from 'date-fns'

const ProdutorDashboard = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const isCAFExpired = () => {
    if (!user?.data_inscricao_caf) return false
    const inscricaoDate = parseISO(user.data_inscricao_caf)
    const expirationDate = addYears(inscricaoDate, 3)
    const today = new Date()
    return today > expirationDate
  }

  const getCAFDaysRemaining = () => {
    if (!user?.data_inscricao_caf) return null
    const inscricaoDate = parseISO(user.data_inscricao_caf)
    const expirationDate = addYears(inscricaoDate, 3)
    const today = new Date()
    const daysRemaining = differenceInDays(expirationDate, today)
    return daysRemaining
  }

  const cafExpired = isCAFExpired()
  const daysRemaining = getCAFDaysRemaining()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar>
          <Restaurant sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Merenda Escolar
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Olá, {user?.nome || 'Produtor'}
        </Typography>

        {cafExpired && (
          <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold">
              Atenção: Seu CAF está expirado!
            </Typography>
            <Typography variant="body2">
              Você precisa atualizar sua data de inscrição do CAF no seu perfil para
              participar dos editais.
            </Typography>
          </Alert>
        )}

        {!cafExpired && daysRemaining !== null && daysRemaining <= 90 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Seu CAF expira em {daysRemaining} dias. Mantenha seus dados atualizados.
            </Typography>
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 3
          }}
        >
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.02)' }
            }}
            onClick={() => navigate('/produtor/editais')}
          >
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Assignment sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Editais
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ver editais disponíveis
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.02)' }
            }}
            onClick={() => navigate('/produtor/perfil')}
          >
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Person sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Meu Perfil
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Atualizar dados cadastrais
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Informações do CAF
          </Typography>
          {user?.caf ? (
            <Box>
              <Typography variant="body1">
                <strong>CAF:</strong> {user.caf}
              </Typography>
              {user?.data_inscricao_caf && (
                <Typography variant="body1" sx={{ mt: 1 }}>
                  <strong>Data de Inscrição:</strong>{' '}
                  {new Date(user.data_inscricao_caf).toLocaleDateString('pt-BR')}
                </Typography>
              )}
              <Typography
                variant="body1"
                sx={{ mt: 1, color: cafExpired ? 'error.main' : 'success.main' }}
              >
                <strong>Status:</strong>{' '}
                {cafExpired ? 'Expirado' : 'Ativo'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              CAF não cadastrado. Atualize seu perfil.
            </Typography>
          )}
        </Paper>
      </Container>
    </Box>
  )
}

export default ProdutorDashboard

